import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Alert from '../ui/Alert';
import FieldToolbar from '../editor/FieldToolbar';
import DraggableField from '../editor/DraggableField';
import { generateFieldId } from '../../utils/idGenerator';
import './FieldAssignment.css';

// Set up the worker for PDF.js if not already set
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

const FieldAssignment = ({ 
  document, 
  recipients = [], 
  currentUser,
  onAssignField,
  onContinue,
  loading = false
}) => {
  const [activeRecipient, setActiveRecipient] = useState(null);
  const [error, setError] = useState(null);
  const [showOwnerFields, setShowOwnerFields] = useState(false);
  const [ownerFieldsComplete, setOwnerFieldsComplete] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pages, setPages] = useState([]);
  const [activeFieldType, setActiveFieldType] = useState(null);
  const [isDraggingFromToolbar, setIsDraggingFromToolbar] = useState(false);
  const [dragOverPage, setDragOverPage] = useState(null);
  const [dragSourceType, setDragSourceType] = useState(null);
  const [fields, setFields] = useState(document?.fields || []);
  
  const documentContainerRef = useRef(null);
  
  // Set the initial active recipient
  useEffect(() => {
    // Filter out any empty recipients
    const validRecipients = recipients.filter(r => r.email && r.email.trim() !== '');
    
    if (validRecipients.length > 0) {
      // Start with first recipient
      setActiveRecipient(validRecipients[0].email);
    }
  }, [recipients]);
  
  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPages(Array.from(new Array(numPages), (_, index) => index + 1));
  };
  
  const handleRecipientSelect = (recipientEmail) => {
    setActiveRecipient(recipientEmail);
  };
  
  // Handle adding a new field
  const handleAddField = (fieldType) => {
    setActiveFieldType(fieldType);
  };

  // Handle toolbar field drag start
  const handleToolbarDragStart = (fieldType) => {
    setActiveFieldType(fieldType);
    setIsDraggingFromToolbar(true);
    setDragSourceType('new');
  };

  // Handle document drag over
  const handleDragOver = (e, pageNumber) => {
    e.preventDefault();
    // Set the drag over page for visual feedback
    setDragOverPage(pageNumber);
    
    // Set the dropEffect based on the drag source type
    const effect = dragSourceType === 'new' ? 'copy' : 'move';
    e.dataTransfer.dropEffect = effect;
  };

  // Handle document drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverPage(null);
  };

  // Handle document drop
  const handleDrop = (e, pageNumber) => {
    e.preventDefault();
    
    // Reset drag over state
    setDragOverPage(null);
    
    // Get the drop position relative to the document page
    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();
    
    // Check if this is a new field from toolbar or an existing field being moved
    const isExistingField = dragSourceType === 'existing';
    
    // Try to get the JSON data from the dataTransfer
    let fieldId, fieldType;
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        const fieldData = JSON.parse(jsonData);
        fieldId = fieldData.fieldId;
        fieldType = fieldData.fieldType;
      }
    } catch (err) {
      // Try to get data from text/plain as fallback
      const plainData = e.dataTransfer.getData('text/plain');
      
      if (plainData && plainData.includes(':')) {
        // Format might be "id:type:isNew" or "type:new"
        const parts = plainData.split(':');
        if (parts.length >= 2) {
          if (parts[1] === 'new') {
            fieldType = parts[0];
          } else {
            fieldId = parts[0];
            fieldType = parts[1];
          }
        }
      } else if (plainData) {
        // Just a simple ID or type
        if (isExistingField) {
          fieldId = plainData;
          // Try to find the field type from existing fields
          const field = fields.find(f => f.id === plainData);
          if (field) {
            fieldType = field.type;
          }
        } else {
          fieldType = plainData;
        }
      }
    }
    
    // If we still don't have a field type, use the active field type
    if (!fieldType && activeFieldType) {
      fieldType = activeFieldType;
    }
    
    // If we don't have either field ID or type, we can't proceed
    if (!fieldId && !fieldType) {
      return;
    }
    
    // Get the offset from the dataTransfer object or use default values
    let offsetX = 0, offsetY = 0;
    try {
      const offsetData = e.dataTransfer.getData('offset');
      if (offsetData) {
        const offset = JSON.parse(offsetData);
        offsetX = offset.x || 0;
        offsetY = offset.y || 0;
      }
    } catch (err) {
      // Try to get individual offset values
      try {
        offsetX = parseInt(e.dataTransfer.getData('offsetX')) || 0;
        offsetY = parseInt(e.dataTransfer.getData('offsetY')) || 0;
      } catch (offsetErr) {
        // Continue with default values
      }
    }
    
    // Get field dimensions based on type
    let fieldWidth = 120, fieldHeight = 40;
    if (fieldType === 'signature') {
      fieldWidth = 150;
      fieldHeight = 60;
    } else if (fieldType === 'initial') {
      fieldWidth = 60;
      fieldHeight = 40;
    }
    
    // Calculate the position, accounting for the offset and centering the field
    const x = offsetX ? (e.clientX - rect.left - offsetX) : (e.clientX - rect.left - (fieldWidth / 2));
    const y = offsetY ? (e.clientY - rect.top - offsetY) : (e.clientY - rect.top - (fieldHeight / 2));
    
    // Ensure position is within bounds
    const maxWidth = rect.width;
    const maxHeight = rect.height;
    
    // Constrain position to ensure field stays within document bounds
    const constrainedX = Math.max(0, Math.min(x, maxWidth - fieldWidth));
    const constrainedY = Math.max(0, Math.min(y, maxHeight - fieldHeight));
    
    if (isExistingField && fieldId) {
      // Update existing field position
      const updatedFields = fields.map(field => {
        if (field.id === fieldId) {
          return {
            ...field,
            position: { x: constrainedX, y: constrainedY },
            pageNumber
          };
        }
        return field;
      });
      
      setFields(updatedFields);
    } else {
      // Create a new field
      const newField = {
        id: generateFieldId(),
        type: fieldType,
        position: { x: constrainedX, y: constrainedY },
        pageNumber,
        createdAt: new Date().toISOString(),
        recipientEmail: activeRecipient,
        assignedTo: activeRecipient
      };
      
      // Add the field to the state
      setFields(prevFields => [...prevFields, newField]);
      
      // Assign the field to the active recipient
      if (onAssignField) {
        onAssignField(newField.id, activeRecipient);
      }
    }
    
    // Reset active field type and drag state after drop
    setActiveFieldType(null);
    setIsDraggingFromToolbar(false);
    setDragSourceType(null);
  };

  // Handle clicking on the document to place a field
  const handleDocumentClick = (e, pageNumber) => {
    if (!activeFieldType) return;
    
    // Get the click position relative to the document page
    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();
    
    // Get field dimensions based on type
    let fieldWidth = 120, fieldHeight = 40;
    if (activeFieldType === 'signature') {
      fieldWidth = 150;
      fieldHeight = 60;
    } else if (activeFieldType === 'initial') {
      fieldWidth = 60;
      fieldHeight = 40;
    }
    
    // Calculate position with the cursor at the center of the field
    const x = e.clientX - rect.left - (fieldWidth / 2);
    const y = e.clientY - rect.top - (fieldHeight / 2);
    
    // Ensure position is within bounds
    const maxWidth = rect.width;
    const maxHeight = rect.height;
    const constrainedX = Math.max(0, Math.min(x, maxWidth - fieldWidth));
    const constrainedY = Math.max(0, Math.min(y, maxHeight - fieldHeight));
    
    // Create a new field
    const newField = {
      id: generateFieldId(),
      type: activeFieldType,
      position: { x: constrainedX, y: constrainedY },
      pageNumber,
      createdAt: new Date().toISOString(),
      recipientEmail: activeRecipient,
      assignedTo: activeRecipient
    };
    
    // Add the field to the state
    setFields(prevFields => [...prevFields, newField]);
    
    // Assign the field to the active recipient
    if (onAssignField) {
      onAssignField(newField.id, activeRecipient);
    }
    
    // Deselect the field tool after placing a field
    setActiveFieldType(null);
  };
  
  const handleFieldDelete = (fieldId) => {
    setFields(prevFields => prevFields.filter(field => field.id !== fieldId));
  };
  
  const handleFieldPositionChange = (fieldId, newPosition, pageNumber) => {
    setFields(prevFields => 
      prevFields.map(field => 
        field.id === fieldId 
          ? { ...field, position: newPosition, pageNumber: pageNumber || field.pageNumber } 
          : field
      )
    );
  };
  
  const handleContinue = () => {
    // Check if all recipients have fields
    const assignedRecipients = new Set();
    
    fields.forEach(field => {
      if (field.assignedTo) {
        assignedRecipients.add(field.assignedTo);
      }
    });
    
    // Filter out any empty recipients
    const validRecipients = recipients.filter(r => r.email && r.email.trim() !== '');
    
    // Check if all recipients have fields
    const missingRecipients = validRecipients.filter(r => !assignedRecipients.has(r.email));
    if (missingRecipients.length > 0) {
      setError(`Please assign at least one field for ${missingRecipients.map(r => r.name || r.email).join(', ')}`);
      return;
    }
    
    // Update document with fields
    if (onContinue) {
      onContinue(fields);
    }
  };
  
  // Generate colors for recipients
  const getRecipientColor = (index) => {
    const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336'];
    return colors[index % colors.length];
  };
  
  return (
    <div className="field-assignment">
      <h2 className="field-assignment-title">
        Add Signature Fields
      </h2>
      
      {error && (
        <Alert 
          type="error"
          title="Error"
          message={error}
          dismissible={true}
          onDismiss={() => setError(null)}
          icon={null}
          action={null}
        />
      )}
      
      <div className="field-assignment-container">
        <div className="field-assignment-sidebar">
          <Card 
            className="signers-card"
            title="Signers"
            subtitle=""
            footer=""
          >
            <div className="signers-list">
              {recipients.filter(r => r.email && r.email.trim() !== '').map((recipient, index) => {
                const color = recipient.color || getRecipientColor(index);
                const hasFields = fields.some(field => field.assignedTo === recipient.email);
                
                return (
                  <div 
                    key={recipient.email}
                    className={`signer-item ${activeRecipient === recipient.email ? 'active' : ''} ${hasFields ? 'has-fields' : ''}`}
                    onClick={() => handleRecipientSelect(recipient.email)}
                    style={{ borderLeftColor: color }}
                  >
                    <div className="signer-info">
                      <div className="signer-name">{recipient.name || recipient.email}</div>
                      <div className="signer-email">{recipient.email}</div>
                    </div>
                    {hasFields && (
                      <div className="signer-status">✓</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
          
          <div className="field-assignment-instructions">
            <h4>Instructions</h4>
            <ol>
              <li>Select a signer from the list</li>
              <li>Add signature fields to the document</li>
              <li>Assign each field to the selected signer</li>
              <li>Repeat for all signers</li>
            </ol>
          </div>
          
          <FieldToolbar 
            onAddField={handleAddField} 
            onDragStart={handleToolbarDragStart}
          />
        </div>
        
        <div className="field-assignment-content">
          <div className="active-signer-info">
            <div className="active-signer-label">Currently adding fields for:</div>
            <div 
              className="active-signer-name"
              style={{ 
                backgroundColor: recipients.find(r => r.email === activeRecipient)?.color || 
                  getRecipientColor(recipients.findIndex(r => r.email === activeRecipient))
              }}
            >
              {recipients.find(r => r.email === activeRecipient)?.name || activeRecipient || 'Select a signer'}
            </div>
          </div>
          
          <div className="document-preview" ref={documentContainerRef}>
            <Document
              file={document.file_data && document.file_data.startsWith('STORAGE_URL:') 
                ? document.file_data.substring('STORAGE_URL:'.length) 
                : document.fileData}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="loading-spinner"></div>}
              error={<Alert 
                type="error" 
                message="Failed to load PDF document" 
                title="Error"
                icon={null}
                action={null}
                onDismiss={() => {}}
              />}
            >
              <div className="document-pages-container">
                {pages.map(pageNumber => (
                  <div key={pageNumber} className="document-page-wrapper">
                    <div 
                      className={`document-page-overlay ${dragOverPage === pageNumber ? 'drag-over' : ''}`}
                      onClick={(e) => handleDocumentClick(e, pageNumber)}
                      onDragOver={(e) => handleDragOver(e, pageNumber)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, pageNumber)}
                    >
                      {fields
                        .filter(field => field.pageNumber === pageNumber)
                        .map(field => (
                          <DraggableField
                            key={field.id}
                            id={field.id}
                            type={field.type}
                            position={field.position}
                            size={field.size}
                            onPositionChange={handleFieldPositionChange}
                            onSizeChange={() => {}}
                            onDelete={handleFieldDelete}
                            pageNumber={pageNumber}
                            onDragStart={() => setDragSourceType('existing')}
                            borderColor={
                              recipients.find(r => r.email === field.assignedTo)?.color ||
                              getRecipientColor(recipients.findIndex(r => r.email === field.assignedTo))
                            }
                          />
                        ))
                      }
                    </div>
                    <Page 
                      pageNumber={pageNumber} 
                      width={800}
                      className="document-page"
                    />
                  </div>
                ))}
              </div>
            </Document>
          </div>
        </div>
      </div>
      
      <div className="field-assignment-actions">
        <Button 
          onClick={handleContinue}
          disabled={loading}
          loading={loading}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default FieldAssignment;
